import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, RefreshControl } from 'react-native';
import { Text, Card, Title, Paragraph, Button, useTheme, ActivityIndicator, Badge } from 'react-native-paper';
import { getActiveVisits, getPendingVisits, getAllCategories, getAllUsers, getAllChildren } from '../../api/adminAPI';
import { useAuth } from '../../context/AuthContext';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const AdminDashboardScreen = ({ navigation }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    activeVisits: 0,
    pendingVisits: 0,
    categories: 0,
    users: 0,
    children: 0
  });

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Aktif ziyaretleri al
      const visitsResponse = await getActiveVisits();
      const activeVisitsCount = Array.isArray(visitsResponse.data) ? visitsResponse.data.length : 
                               (visitsResponse.data?.count || visitsResponse.count || 0);
      
      // Onay bekleyen ziyaretleri al
      const pendingVisitsResponse = await getPendingVisits();
      const pendingVisitsCount = Array.isArray(pendingVisitsResponse.data) ? pendingVisitsResponse.data.length : 
                                (pendingVisitsResponse.data?.count || pendingVisitsResponse.count || 0);
      
      // Menü kategorilerini al 
      const categoriesResponse = await getAllCategories();
      const categoriesCount = Array.isArray(categoriesResponse.data) ? categoriesResponse.data.length : 
                             (categoriesResponse.data?.count || categoriesResponse.count || 0);
      
      // Kullanıcıları al
      const usersResponse = await getAllUsers();
      const usersCount = Array.isArray(usersResponse.data) ? usersResponse.data.length : 
                         (usersResponse.data?.count || usersResponse.count || 0);
      
      // Çocukları al
      const childrenResponse = await getAllChildren();
      const childrenCount = Array.isArray(childrenResponse.data) ? childrenResponse.data.length : 
                           (childrenResponse.data?.count || childrenResponse.count || 0);
      
      setStats({
        activeVisits: activeVisitsCount,
        pendingVisits: pendingVisitsCount,
        categories: categoriesCount,
        users: usersCount,
        children: childrenCount
      });

     {/*console.log('Dashboard İstatistikleri:', {
        activeVisits: activeVisitsCount,
        pendingVisits: pendingVisitsCount,
        categories: categoriesCount,
        users: usersCount,
        children: childrenCount
      });
      */}
    } catch (error) {
      console.error('Dashboard veri yükleme hatası:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    loadData();
  }, []);

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Yükleniyor...</Text>
      </View>
    );
  }

  return (
    <ScrollView 
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      <Card style={styles.headerCard}>
        <Card.Content>
          <Title style={styles.welcomeTitle}>Hoş Geldiniz, {user?.name || user?.username}!</Title>
          <Paragraph>Oyun Parkı Yönetim Paneline hoş geldiniz. Buradan tüm işlemleri yönetebilirsiniz.</Paragraph>
        </Card.Content>
      </Card>

      <View style={styles.statsContainer}>
        <Card style={styles.statsCard}>
          <Card.Content style={styles.statsCardContent}>
            <MaterialCommunityIcons name="account-group" size={32} color={theme.colors.primary} />
            <View style={styles.statsTextContainer}>
              <Paragraph>Aktif Kullanıcılar</Paragraph>
              <Title>{stats.users}</Title>
            </View>
          </Card.Content>
          <Card.Actions>
            <Button onPress={() => navigation.navigate('UsersManagement')}>
              Kullanıcıları Yönet
            </Button>
          </Card.Actions>
        </Card>

        <Card style={styles.statsCard}>
          <Card.Content style={styles.statsCardContent}>
            <MaterialCommunityIcons name="calendar-check" size={32} color="#4CAF50" />
            <View style={styles.statsTextContainer}>
              <Paragraph>Aktif Ziyaretler</Paragraph>
              <Title>{stats.activeVisits}</Title>
            </View>
          </Card.Content>
          <Card.Actions>
            <Button onPress={() => navigation.navigate('VisitsManagement')}>
              Ziyaretleri Yönet
            </Button>
          </Card.Actions>
        </Card>

        <Card style={styles.statsCard}>
          <Card.Content style={styles.statsCardContent}>
            <MaterialCommunityIcons name="clock-outline" size={32} color="#FFA000" />
            <View style={styles.statsTextContainer}>
              <Paragraph>Onay Bekleyen Ziyaretler</Paragraph>
              <Title>
                {stats.pendingVisits} 
                {stats.pendingVisits > 0 && <Badge size={22} style={styles.badge}>{stats.pendingVisits}</Badge>}
              </Title>
            </View>
          </Card.Content>
          <Card.Actions>
            <Button onPress={() => navigation.navigate('VisitsManagement', { screen: 'PendingVisits' })}>
              Onay Bekleyenleri Gör
            </Button>
          </Card.Actions>
        </Card>
      </View>

      <View style={styles.quickActionsContainer}>
        <Title style={styles.sectionTitle}>Hızlı İşlemler</Title>
        <View style={styles.quickActionsGrid}>
          <Card style={styles.actionCard} onPress={() => navigation.navigate('ChildrenManagement')}>
            <Card.Content style={styles.actionCardContent}>
              <MaterialCommunityIcons name="account-child" size={36} color="#2196F3" />
              <Title style={styles.actionTitle}>Çocuklar</Title>
              <Paragraph>Çocuk profilleri ve kayıtları</Paragraph>
              {stats.children > 0 && <Badge style={[styles.actionBadge, {backgroundColor: '#2196F3'}]}>{stats.children}</Badge>}
            </Card.Content>
          </Card>

          <Card style={styles.actionCard} onPress={() => navigation.navigate('UsersManagement')}>
            <Card.Content style={styles.actionCardContent}>
              <MaterialCommunityIcons name="account-multiple" size={36} color="rgb(125, 76, 236)" />
              <Title style={styles.actionTitle}>Aktif Kullanıcılar</Title>
              <Paragraph>Sisteme kayıtlı kullanıcılar</Paragraph>
              {stats.users > 0 && <Badge style={[styles.actionBadge, {backgroundColor: 'rgb(125, 76, 236)'}]}>{stats.users}</Badge>}
            </Card.Content>
          </Card>

        
          <Card style={styles.actionCard} onPress={() => navigation.navigate('VisitsManagement', { screen: 'ActiveVisits' })}>
            <Card.Content style={styles.actionCardContent}>
              <MaterialCommunityIcons name="timer" size={36} color="rgb(125, 76, 236)" />
              <Title style={styles.actionTitle}>Aktif Ziyaretler</Title>
              <Paragraph>Şu an parkta olanlar</Paragraph>
              {stats.activeVisits > 0 && <Badge style={[styles.actionBadge, {backgroundColor: 'rgb(125, 76, 236)'}]}>{stats.activeVisits}</Badge>}
            </Card.Content>
          </Card>


          <Card style={styles.actionCard} onPress={() => navigation.navigate('VisitsManagement', { screen: 'PendingVisits' })}>
            <Card.Content style={styles.actionCardContent}>
              <MaterialCommunityIcons name="clock-alert-outline" size={36} color="rgb(125, 76, 236)" />
              <Title style={styles.actionTitle}>Onay Bekleyenler</Title>
              <Paragraph>Onay bekleyen ziyaretler</Paragraph>
              {stats.pendingVisits > 0 && <Badge style={[styles.actionBadge, {backgroundColor: 'rgb(125, 76, 236)'}]}>{stats.pendingVisits}</Badge>}
            </Card.Content>
          </Card>
          <Card style={styles.actionCard} onPress={() => navigation.navigate('MenuManagement', { screen: 'AdminMenu' })}>
            <Card.Content style={styles.actionCardContent}>
              <MaterialCommunityIcons name="food-fork-drink" size={36} color="rgb(125, 76, 236)" />
              <Title style={styles.actionTitle}>Menü</Title>
              <Paragraph>Kategoriler ve ürünler</Paragraph>
            </Card.Content>
          </Card>


          <Card style={styles.actionCard} onPress={() => navigation.navigate('VisitsManagement', { screen: 'VisitHistory' })}>
            <Card.Content style={styles.actionCardContent}>
              <MaterialCommunityIcons name="history" size={36} color="rgb(125, 76, 236)" />
              <Title style={styles.actionTitle}>Ziyaret Geçmişi</Title>
              <Paragraph>Geçmiş ziyaretler</Paragraph>
            </Card.Content>
          </Card>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  contentContainer: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  headerCard: {
    marginBottom: 16,
    elevation: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  welcomeTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  sectionTitle: {
    marginBottom: 12,
    fontWeight: 'bold',
  },
  statsContainer: {
    marginBottom: 24,
  },
  statsCard: {
    marginBottom: 12,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  statsCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statsTextContainer: {
    marginLeft: 16,
    flex: 1,
  },
  badge: {
    marginLeft: 8,
    backgroundColor: '#FFA000',
    color: 'white'
  },
  quickActionsContainer: {
    marginBottom: 20,
  },
  quickActionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  actionCard: {
    width: '48%',
    marginBottom: 16,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  actionCardContent: {
    alignItems: 'center',
    padding: 16,
    position: 'relative'
  },
  actionTitle: {
    marginTop: 12,
    fontSize: 16,
    fontWeight: 'bold',
  },
  actionBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    fontSize: 12,
    color: 'white'
  }
});

export default AdminDashboardScreen;
